import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { setDataState, setLoadedData } from '../../../state/Slices/dataSlice'
import { db } from '../../config'
import { getMenuData, getMenuDataForBot } from '../getMenuData'
import { getMenuItemData } from '../getMenuItemData'
import { getModifierData, getModifierDataForBot } from '../getModifierData'
import { formatRestaurantData } from '../getRestaurantData'
import { getTaxData, getTaxDataForBot } from '../getTaxData'
import _ from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { getOrgData, getOrgDataForBot } from '../getOrgData'
import { getMenuItemCategories } from '../getMenuItemCategories'
import { getMenuItemCategoriesForBot } from '../getMenuItemCategories'
import { getMenuItemDataForBot } from '../getMenuItemData'
import { transformFirestoreResponse } from '../../utils/firestoreTransform'
import { detectBot } from '../../../utils/botDetection'
import { firestoreGet } from '../../../utils/firestoreApi'

function arrayFromObjects(restaurantObject) {
	var arrayOfObjects = []
	for (const id in restaurantObject) {
		if (id !== 'defaultRestaurant') {
			const restaurant = restaurantObject[id]
			const newObject = { id, ...restaurant }
			arrayOfObjects.push(newObject)
		}
	}
	return arrayOfObjects
}

const useRestaurantListeners = (setSpinner, setRestaurants, setMenuItems, setModifierItems) => {
	const dispatch = useAppDispatch()
	const loadingState = useRef(new Map())
	const emptyCollections = useRef(new Map())

	const [menuCategories, setMenuCategories] = useState(null)

	const isBot = detectBot()

	const updateMenuCategories = (newCategories: any[]) => {
		setMenuCategories(prevCategories => {
			const updatedCategories = prevCategories ? [...prevCategories] : []

			newCategories.forEach(newCategory => {
				const existingCategoryIndex = updatedCategories.findIndex(
					category => category.id === newCategory.id
				)

				if (existingCategoryIndex === -1) {
					// Category doesn't exist, add it to the array
					updatedCategories.push(newCategory)
				} else {
					// Category exists, merge the arrays
					const existingCategory = updatedCategories[existingCategoryIndex]
					const updatedImage =
						!existingCategory.image?.url && newCategory.image?.url
							? newCategory.image
							: existingCategory.image
					updatedCategories[existingCategoryIndex] = {
						...existingCategory,
						items: [...new Set([...existingCategory.items, ...newCategory.items])],
						restaurantIds: [
							...new Set([...existingCategory.restaurantIds, ...newCategory.restaurantIds]),
						],
						image: updatedImage,
					}
				}
			})

			return updatedCategories
		})
	}

	useEffect(() => {
		//console.log(menuCategories)
		dispatch(setDataState({ menuItemCategories: menuCategories }))
	}, [menuCategories])

	useEffect(() => {
		const checkAllLoaded = () => {
			const allRestaurantStates = Array.from(loadingState.current.entries())

			if (allRestaurantStates.length === 0) {
				setSpinner(false)
				return true
			}

			const isAllLoaded = allRestaurantStates.every(([restaurantId, restaurantState]) => {
				const notLoaded = Object.entries(restaurantState)
					.filter(([collection, isLoaded]) => {
						const isEmpty = emptyCollections.current.get(restaurantId)?.includes(collection)
						return !isLoaded && !isEmpty
					})
					.map(([collection]) => collection)

				return notLoaded.length === 0
			})

			if (isAllLoaded) {
				setSpinner(false)
			}

			return isAllLoaded
		}

		const markAsLoaded = (restaurantId, collectionName) => {
			if (!loadingState.current.has(restaurantId)) {
				loadingState.current.set(restaurantId, {
					Menus: false,
					MenuItems: false,
					Taxes: false,
					ModifierLists: false,
					Restaurants: false,
				})
			}

			const restaurantState = loadingState.current.get(restaurantId)
			restaurantState[collectionName] = true
			loadingState.current.set(restaurantId, restaurantState)

			checkAllLoaded()
		}

		const markCollectionAsEmpty = (restaurantId, collectionName) => {
			if (!emptyCollections.current.has(restaurantId)) {
				emptyCollections.current.set(restaurantId, [])
			}
			const empty = emptyCollections.current.get(restaurantId)
			if (!empty.includes(collectionName)) {
				empty.push(collectionName)
				emptyCollections.current.set(restaurantId, empty)
			}
			checkAllLoaded()
		}

		const removeRestaurantFromTracking = restaurantId => {
			loadingState.current.delete(restaurantId)
			emptyCollections.current.delete(restaurantId)
			checkAllLoaded()
		}

		const processCollectionData = async (restaurantId, collectionName, data, getData) => {
			const processedData = await getData(restaurantId, data)

			// Update state based on collection type
			if (collectionName === 'MenuItems') {
				setMenuItems(prevItems => ({
					...prevItems,
					...processedData,
				}))
			} else if (collectionName === 'ModifierLists') {
				setModifierItems(prevMods => ({
					...prevMods,
					...processedData,
				}))
			}

			// Update store and mark as loaded
			dispatch(setLoadedData({ ...(processedData as object) }))
			markAsLoaded(restaurantId, collectionName)
		}

		const handleCollectionError = (restaurantId, collectionName) => {
			markCollectionAsEmpty(restaurantId, collectionName)
		}

		const createSubcollectionListener = (restaurantId, collectionName, getData) => {
			if (!loadingState.current.has(restaurantId)) return null

			const collectionRef = collection(db, 'Restaurants-v2', restaurantId, collectionName)
			return onSnapshot(
				collectionRef,
				async snapshot => {
					if (snapshot.empty) {
						handleCollectionError(restaurantId, collectionName)
						return
					}
					await processCollectionData(restaurantId, collectionName, snapshot, getData)
				},
				() => handleCollectionError(restaurantId, collectionName)
			)
		}

		const createSubcollectionListenerForBot = (restaurantId, collectionName, getData) => {
			if (!loadingState.current.has(restaurantId)) return null

			const endpoint = `Restaurants-v2/${restaurantId}/${collectionName}`

			firestoreGet(endpoint)
				.then(async result => {
					if (!result.documents) {
						handleCollectionError(restaurantId, collectionName)
						return
					}
					await processCollectionData(restaurantId, collectionName, result.documents, getData)
				})
				.catch(() => handleCollectionError(restaurantId, collectionName))

			return null
		}

		const reorderObject = (obj, idArray) => {
			return Object.fromEntries(idArray.map(id => [id, obj[id]]))
		}

		const initializeData = async () => {
			const dataSources = isBot
				? [getOrgDataForBot(), getMenuItemCategoriesForBot()]
				: [getOrgData(), getMenuItemCategories()]
			const [orgData, menuItemCategories] = await Promise.all(dataSources)

			const data: any = orgData
			const restaurantIds = data?.restaurants
			if (!restaurantIds?.length) {
				setSpinner(false)
				return
			}

			restaurantIds.forEach(restaurantId => {
				loadingState.current.set(restaurantId, {
					Menus: false,
					MenuItems: false,
					Taxes: false,
					ModifierLists: false,
					Restaurants: false,
				})
				emptyCollections.current.set(restaurantId, [])
			})

			// Common logic for processing restaurant data
			const processRestaurantData = (restaurantIds: string[], processedDocuments) => {
				const restaurants = {}
				const foundRestaurantIds = new Set()

				processedDocuments.forEach(({ id, data }) => {
					if (data && !data.isDeleted) {
						const restaurantReturn = formatRestaurantData({
							...data,
							id,
						})
						restaurants[id] = restaurantReturn
						foundRestaurantIds.add(id)
						markAsLoaded(id, 'Restaurants')
					}
				})

				// Remove untracked restaurants
				restaurantIds.forEach(id => {
					if (!foundRestaurantIds.has(id)) {
						removeRestaurantFromTracking(id)
					}
				})

				// Update state and dispatch actions
				const restaurantArray = arrayFromObjects(reorderObject(restaurants, restaurantIds))
				setRestaurants(restaurants)
				dispatch(
					setLoadedData({
						...restaurants,
						restaurants: restaurantArray,
					})
				)
				dispatch(setDataState({ restaurants: restaurantArray }))
			}

			const createRestaurantListener = (restaurantIds: string[]) => {
				const collectionRef = collection(db, 'Restaurants-v2')
				const q = query(collectionRef, where('__name__', 'in', restaurantIds))

				return onSnapshot(q, async snapshot => {
					const processedDocuments = snapshot.docs.map(doc => ({
						id: doc.id,
						data: doc.data(),
					}))

					processRestaurantData(restaurantIds, processedDocuments)
				})
			}

			const createRestaurantListenerForBot = (restaurantIds: string[]) => {
				const promises = restaurantIds.map(restaurantId => {
					const endpoint = `Restaurants-v2/${restaurantId}`

					return firestoreGet(endpoint)
						.then(result => ({
							id: restaurantId,
							data: transformFirestoreResponse(result),
						}))
						.catch(error => {
							console.error(`Error fetching restaurant ${restaurantId}:`, error)
							return { id: restaurantId, data: null }
						})
				})

				return Promise.all(promises)
					.then(results => {
						processRestaurantData(restaurantIds, results)
					})
					.catch(error => {
						console.error('Error fetching restaurants:', error)
					})
			}

			const unsubscribeFunctions = []
			const unsubRestaurant = isBot
				? createRestaurantListenerForBot(restaurantIds)
				: createRestaurantListener(restaurantIds)
			unsubscribeFunctions.push(unsubRestaurant)

			const functionMap = {
				createListener: isBot ? createSubcollectionListenerForBot : createSubcollectionListener,
				getMenuData: isBot ? getMenuDataForBot : getMenuData,
				getMenuItemData: isBot ? getMenuItemDataForBot : getMenuItemData,
				getTaxData: isBot ? getTaxDataForBot : getTaxData,
				getModifierData: isBot ? getModifierDataForBot : getModifierData,
			}

			await Promise.all(
				restaurantIds.map(async restaurantId => {
					const [unsubMenus, unsubMenuItems, unsubTaxes, unsubMods] = await Promise.all([
						functionMap.createListener(restaurantId, 'Menus', (id, result) =>
							functionMap.getMenuData(id, result)
						),
						functionMap.createListener(restaurantId, 'MenuItems', (id, result) => {
							const itemPromise = functionMap.getMenuItemData(menuItemCategories, id, result)
							return itemPromise.then((promise: any) => {
								updateMenuCategories(promise.menuItemCategories)
								return promise
							})
						}),
						functionMap.createListener(restaurantId, 'Taxes', (id, result) =>
							functionMap.getTaxData(id, result)
						),
						functionMap.createListener(restaurantId, 'ModifierLists', (id, result) =>
							functionMap.getModifierData(id, result)
						),
					])

					;[unsubMenus, unsubMenuItems, unsubTaxes, unsubMods]
						.filter(Boolean)
						.forEach(unsub => unsubscribeFunctions.push(unsub))
				})
			)

			return () => {
				unsubscribeFunctions.forEach(unsubscribe => unsubscribe())
			}
		}

		initializeData()
	}, [])
}

export default useRestaurantListeners
